import { motion } from "framer-motion";
import { useT } from "@/i18n/useLocale";

import sapLogo from "@/assets/logos/sap.webp";
import oracleLogo from "@/assets/logos/oracle.webp";
import dynamicsLogo from "@/assets/logos/dynamics365.webp";
import netsuiteLogo from "@/assets/logos/netsuite.webp";
import odooLogo from "@/assets/logos/odoo.webp";
import finnegansLogo from "@/assets/logos/finnegans.webp";
import softlandLogo from "@/assets/logos/softland.webp";

export const ERP_LOGOS = [
  { src: sapLogo, alt: "SAP" },
  { src: oracleLogo, alt: "Oracle" },
  { src: dynamicsLogo, alt: "Microsoft Dynamics 365" },
  { src: netsuiteLogo, alt: "NetSuite" },
  { src: finnegansLogo, alt: "Finnegans" },
  { src: odooLogo, alt: "Odoo" },
  { src: softlandLogo, alt: "Softland" },
];

export function HeroFacturas() {
  const t = useT();
  const h = t.facturas.hero;

  return (
    <section id="top" className="relative overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-20">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-40 right-0 h-[460px] w-[460px] rounded-full bg-accent/15 blur-3xl" />
      </div>

      <div className="mx-auto max-w-5xl px-6 text-center lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-4 text-xs uppercase tracking-[0.18em] text-ink-soft/70"
        >
          {h.eyebrow}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="font-display text-4xl leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-[64px]"
        >
          {h.titlePre}
          <em className="not-italic text-gradient-blue">{h.titleAccent}</em>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mx-auto mt-8 max-w-3xl text-lg leading-relaxed text-ink-soft"
        >
          {h.desc}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <a
            href="#demo"
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90"
          >
            {h.ctaDemo}
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </a>
          <a
            href="#flujo"
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-background/60 px-6 py-3 text-sm font-medium text-ink backdrop-blur transition-all hover:border-primary/30"
          >
            {h.ctaFlow}
          </a>
        </motion.div>

        <motion.dl
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mx-auto mt-16 grid max-w-3xl gap-8 sm:grid-cols-3"
        >
          {h.stats.map((s) => (
            <div key={s.l}>
              <dt className="font-display text-4xl leading-none tracking-tight text-gradient-blue">
                {s.v}
              </dt>
              <dd className="mt-3 text-sm leading-relaxed text-ink-soft">{s.l}</dd>
            </div>
          ))}
        </motion.dl>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16"
        >
          <p className="mb-6 text-xs uppercase tracking-[0.18em] text-ink-soft/60">
            {h.erpLabel}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {ERP_LOGOS.map((l) => (
              <img
                key={l.alt}
                src={l.src}
                alt={l.alt}
                loading="lazy"
                className="h-7 w-auto opacity-60 grayscale transition-all hover:opacity-100 hover:grayscale-0 lg:h-8"
              />
            ))}
          </div>
        </motion.div>

        {/* Resumen destinado a buscadores y asistentes con IA: no se muestra en pantalla. */}
        <aside
          aria-label={h.summaryLabel}
          className="speakable-summary sr-only"
        >
          <p>{h.summaryLabel}</p>
          <p>{h.summary}</p>
        </aside>
      </div>
    </section>
  );
}
